import { FIELDS } from './fields'
import type { HeaderConfidence } from './types'

/**
 * Rozhodne, jestli je první řádek hlavička.
 *
 * Chyba je tady tichá a drahá — špatný odhad buď sní jednu vstupenku (považoval
 * data za hlavičku), nebo rozsype celý import (považoval hlavičku za data).
 * Proto se kombinují dva nezávislé signály a při nejistotě se vrací `low`,
 * na což UI reaguje dotazem místo hádání.
 */
export function detectHeader(rows: string[][]): { hasHeader: boolean; confidence: HeaderConfidence } {
    if (rows.length === 0) return { hasHeader: false, confidence: 'low' }
    // Jediný řádek nelze s ničím porovnat.
    if (rows.length === 1) return { hasHeader: false, confidence: 'low' }

    const first = rows[0]
    const sample = rows.slice(1, 11)

    const aliasScore = aliasMatchRatio(first)
    const { contrast, informative } = typeContrast(first, sample)

    // Silná shoda s názvy sloupců, které známe z reálných exportů.
    if (aliasScore >= 0.5) return { hasHeader: true, confidence: 'high' }

    // Žádný sloupec s netextovými daty → typový signál nemá o co se opřít
    // (soubor samých slov). Bez druhého signálu se nehádá.
    if (informative === 0) return { hasHeader: aliasScore > 0, confidence: 'low' }

    // Hlavička je typicky celá textová, zatímco data mají v týchž pozicích
    // čísla/datumy/e-maily. Výrazný kontrast = hlavička i bez známých názvů.
    if (contrast >= 0.5) return { hasHeader: true, confidence: aliasScore > 0 ? 'high' : 'low' }

    // Žádná shoda názvů a první řádek vypadá jako data.
    if (aliasScore === 0 && contrast <= 0.1) return { hasHeader: false, confidence: 'high' }

    return { hasHeader: aliasScore > 0, confidence: 'low' }
}

/** Podíl buněk prvního řádku, které odpovídají známému aliasu. */
function aliasMatchRatio(row: string[]): number {
    const known = new Set(FIELDS.flatMap(f => f.aliases.map(a => normalise(a))))
    const nonEmpty = row.filter(c => c.trim() !== '')
    if (nonEmpty.length === 0) return 0
    const hits = nonEmpty.filter(c => known.has(normalise(c))).length
    return hits / nonEmpty.length
}

/**
 * Jak moc se typový profil prvního řádku liší od zbytku.
 *
 * Počítá jen sloupce, kde jsou data konzistentně netextová — tam je rozdíl
 * průkazný. Sloupce s textem všude nic neříkají.
 */
function typeContrast(first: string[], sample: string[][]): { contrast: number; informative: number } {
    if (sample.length === 0) return { contrast: 0, informative: 0 }

    let informative = 0
    let differing = 0

    for (let c = 0; c < first.length; c++) {
        const column = sample.map(r => r[c] ?? '').filter(v => v.trim() !== '')
        if (column.length === 0) continue

        const nonTextRatio = column.filter(v => !isTextual(v)).length / column.length
        if (nonTextRatio < 0.8) continue // data v tomto sloupci jsou text — nevypovídající

        informative++
        if (isTextual(first[c] ?? '')) differing++
    }

    return { contrast: informative === 0 ? 0 : differing / informative, informative }
}

/** Text = ne číslo, ne datum, ne e-mail. */
function isTextual(value: string): boolean {
    const v = value.trim()
    if (v === '') return true
    if (/^[+-]?[\d\s\u00a0]*[.,]?\d+$/.test(v)) return false // číslo (i s mezerami/čárkou)
    if (/^\d{4}-\d{2}-\d{2}/.test(v) || /^\d{1,2}\.\d{1,2}\.\d{4}/.test(v)) return false // datum
    if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) return false // e-mail
    return true
}

function normalise(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
}
